import { useAtom } from 'jotai'
import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import useAtomWithRef from '../../../utils/useAtomWithRef'
import { fullscreenAtom, ownsNFTAtom, videoElementAtom } from './atoms'
import TimelineHover from './TimelineHover'

//* ========================== React component =================================

const Timeline = (): JSX.Element => {
  const [currentProgress, setCurrentProgress] = useState(0.0)

  const [videoElementRef, videoElement] = useAtomWithRef(videoElementAtom)
  const [relativeBufferLength, setRelativeBufferLength] = useState(0.0)
  const [ownsNFT] = useAtom(ownsNFTAtom)
  const [isFullscreen] = useAtom(fullscreenAtom)

  const timelineRef = useRef<HTMLDivElement>(null)

  // Calculate the time range of the buffered video.
  useEffect(() => {
    if (!videoElement) return

    const timeRanges = videoElement.buffered
    if (timeRanges.length > 0) {
      const timeRange = timeRanges.end(timeRanges.length - 1)
      setRelativeBufferLength(timeRange / videoElementRef.current!.duration)
    }
  }, [videoElement?.buffered, videoElement])

  useEffect(() => {
    if (!videoElement) {
      return
    }

    videoElement.addEventListener('timeupdate', () => {
      // We use the duration specified inside the metadata to
      // prevent a bug when the source changes, but is not loaded yet.
      // console.log('timeupdate ', videoElement.currentTime, videoElement.duration)
      if (isNaN(videoElement.duration)) return

      setCurrentProgress(videoElement.currentTime / videoElementRef.current!.duration)
    })
  }, [videoElement])

  return (
    <Wrapper ref={timelineRef}>
      <BackgroundRectangle roundedCorners={!isFullscreen}>
        {/* We can't use a styled component here because the CSS is updated so frequently.*/}
        <TimelineHover timelineRef={timelineRef} />
        <style>
          {`.foreground-rectangle {
            width: ${currentProgress * 100}%;
            height: 100%;
            background: ${ownsNFT ? '#f2f2f2' : '#f2f2f2'};
            position: absolute;
            left: 0;
            top: 0;
            border-radius: 100px;
          }`}
        </style>
        <div className="foreground-rectangle">
          <style>
            {`.circle-head {
            right: 0;
            top: 50%;
            background: ${ownsNFT ? 'white' : 'white'};
            position: absolute;
            width: 12px;
            height: 12px;
            border-radius: 100px;
            transform: translate(50%, -50%);
          }`}
          </style>
          <div className="circle-head" />
        </div>

        <style>
          {`.buffer-rectangle {
              width: ${relativeBufferLength * 100}%;
              height: 100%;
              background-color: #828282;
              position: abosolute;
              left: 0;
              top: 0;
              border-radius: 100px;
             }`}
        </style>
        <div className="buffer-rectangle" />
      </BackgroundRectangle>
    </Wrapper>
  )
}

Timeline.displayName = 'Timeline'

export default Timeline

//* ========================== Styled components ================================

const BackgroundRectangle = styled.div<{ roundedCorners: boolean }>`
  position: relative;
  width: 100%;
  background-color: #4f4f4f;

  border-radius: 100px;

  height: 4px;
`

const Wrapper = styled.div`
  width: 100%;

  height: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  pointer-events: all;

  cursor: none;
`
